import { useState, useEffect, type ReactNode } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  Search,
  Activity,
  Menu,
  X,
  CandlestickChart,
  Compass,
  Wallet,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  MessageSquare,
  Coins,
  Send,
  type LucideIcon,
} from 'lucide-react';
import { Logo } from './Logo';
import { useAuth } from '@/contexts/AuthContext';
import { useCredits } from '@/hooks/useCredits';
import { useMinWidth } from '@/hooks/useMediaQuery';

// Primary sections — the four pillars of the platform
const primaryNavItems: Array<{
  to: string;
  icon: LucideIcon;
  label: string;
  description: string;
  end?: boolean;
  isAlpha?: boolean;
  requiresAuth?: boolean;
}> = [
  { to: '/dashboard', icon: Search, label: 'General Research', description: 'AI-powered research on any topic', end: true },
  { to: '/dashboard/stock-analysis', icon: CandlestickChart, label: 'Stock Analysis', description: 'Deep-dive equity research', requiresAuth: true },
  { to: '/dashboard/strategic', icon: Compass, label: 'Strategic Analysis', description: 'Strategic business analysis', isAlpha: true, requiresAuth: true },
  { to: '/dashboard/portfolio', icon: Wallet, label: 'Portfolio', description: 'Portfolio tracking & insights', isAlpha: true, requiresAuth: true },
];

// Admin-only nav items
const adminNavItems = [
  { to: '/admin/monitoring', icon: Activity, label: 'Monitoring' },
  { to: '/admin/twitter', icon: Send, label: 'Twitter' },
];

interface SidebarProps {
  /** Desktop collapsed mode — icon-only strip */
  isCollapsed?: boolean;
  /** Toggle callback for collapsed state */
  onToggleCollapse?: () => void;
  /** Optional content rendered below the nav section (e.g., session list, CTAs) */
  children?: ReactNode;
}

export function Sidebar({ isCollapsed = false, onToggleCollapse, children }: SidebarProps) {
  const { user } = useAuth();
  const { balance, isUnlimited } = useCredits();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [navExpanded, setNavExpanded] = useState(true);
  const isDesktop = useMinWidth('md');

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  /* ── Collapsed desktop sidebar — icon-only strip ── */
  const collapsedContent = (
    <div className="flex flex-col items-center h-full py-3 gap-1">
      <button
        onClick={onToggleCollapse}
        className="p-1.5 rounded-lg hover:bg-surface-light transition-colors mb-2"
        title="Expand sidebar"
      >
        <img src="/octoverdict-no-bg.png" alt="Vettis" className="w-6 h-6 object-contain" />
      </button>

      <nav className="flex-1 flex flex-col gap-0.5 px-1.5 w-full">
        {primaryNavItems.map(({ to, icon: Icon, label, end, requiresAuth }) => {
          const isLocked = !user && requiresAuth;

          if (isLocked) {
            return (
              <Link
                key={to}
                to="/signup"
                title={`${label} — Sign up to access`}
                className="flex items-center justify-center p-2 rounded-lg transition-colors opacity-40 text-text-muted"
              >
                <Icon className="w-[18px] h-[18px]" />
              </Link>
            );
          }

          return (
            <NavLink
              key={to}
              to={to}
              end={end}
              title={label}
              className={({ isActive }) =>
                clsx(
                  'flex items-center justify-center p-2 rounded-lg transition-colors',
                  isActive
                    ? 'bg-surface-light text-text-primary'
                    : 'text-text-muted hover:bg-surface-light/50 hover:text-text-secondary'
                )
              }
            >
              <Icon className="w-[18px] h-[18px]" />
            </NavLink>
          );
        })}
      </nav>

      <div className="flex flex-col items-center gap-0.5 px-1.5 w-full border-t border-border pt-2">
        {user && (
          <Link
            to="/dashboard/plans"
            title={isUnlimited ? 'Unlimited credits' : `${balance?.toLocaleString() ?? 0} credits`}
            className={clsx(
              'p-2 rounded-lg hover:bg-surface-light hover:text-text-secondary transition-colors',
              !isUnlimited && balance !== null && balance <= 0 ? 'text-error' :
              !isUnlimited && balance !== null && balance < 200 ? 'text-warning' :
              'text-text-muted'
            )}
          >
            <Coins className="w-[18px] h-[18px]" />
          </Link>
        )}
        <Link
          to="/feedback"
          title="Share Feedback"
          className="p-2 rounded-lg text-text-muted hover:bg-surface-light hover:text-text-secondary transition-colors"
        >
          <MessageSquare className="w-[18px] h-[18px]" />
        </Link>
        <button
          onClick={onToggleCollapse}
          className="p-2 rounded-lg text-text-muted hover:bg-surface-light hover:text-text-secondary transition-colors"
          title="Expand sidebar"
        >
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );

  /* ── Expanded sidebar content ── */
  const expandedContent = (
    <>
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <Logo size="sm" linkTo="/dashboard" />
        <div className="flex items-center gap-1.5">
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="hidden md:inline-flex p-2 rounded-lg text-text-muted hover:bg-surface-light hover:text-text-primary transition-colors"
              aria-label="Collapse sidebar"
              title="Collapse sidebar"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}

          <button
            onClick={() => setIsOpen(false)}
            className="md:hidden p-2 rounded-lg text-text-secondary hover:bg-surface-light hover:text-text-primary transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

        <div className={clsx('p-4 overflow-y-auto', !children && 'flex-1')}>
          {/* Collapsible Research header */}
          <button
            onClick={() => setNavExpanded(v => !v)}
            className="flex items-center justify-between w-full px-3 py-1.5 mb-1 text-xs font-medium text-text-muted uppercase tracking-wider hover:text-text-secondary transition-colors"
          >
            <span>Research</span>
            <ChevronDown
              className={clsx(
                'w-3.5 h-3.5 transition-transform duration-200',
                !navExpanded && '-rotate-90'
              )}
            />
          </button>

          {navExpanded ? (
            /* ── Expanded: full nav items ── */
            <nav className="space-y-1">
              {primaryNavItems.map(({ to, icon: Icon, label, description, end, isAlpha, requiresAuth }) => {
                const isLocked = !user && requiresAuth;

                if (isLocked) {
                  return (
                    <Link
                      key={to}
                      to="/signup"
                      className="group flex items-center gap-3 px-3 py-2.5 rounded-lg border-l-2 border-transparent transition-colors duration-150 opacity-40 text-text-muted"
                    >
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-text-muted">
                        <Icon className="w-[18px] h-[18px]" />
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="flex items-center gap-1">
                          <span className="text-[13px] font-medium leading-tight text-text-muted">
                            {label}
                          </span>
                          {isAlpha && (
                            <sup className="text-[7px] font-bold uppercase tracking-wider text-amber-400/40 -top-1 relative ml-0.5 select-none">
                              alpha
                            </sup>
                          )}
                        </span>
                        <span className="text-[11px] text-text-muted/60 leading-tight truncate">
                          {description}
                        </span>
                      </div>
                    </Link>
                  );
                }

                return (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    className={({ isActive }) =>
                      clsx(
                        'group flex items-center gap-3 px-3 py-2.5 rounded-lg border-l-2 border-transparent transition-colors duration-150',
                        isActive
                          ? 'bg-surface-light/50 text-text-primary border-primary/20'
                          : 'text-text-secondary hover:bg-surface-light/30 hover:text-text-primary'
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <div
                          className={clsx(
                            'w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors',
                            isActive
                              ? 'text-text-primary'
                              : 'text-text-muted group-hover:text-text-secondary'
                          )}
                        >
                          <Icon className="w-[18px] h-[18px]" />
                        </div>
                        <div className="flex flex-col min-w-0">
                          <span className="flex items-center gap-1">
                            <span
                              className={clsx(
                                'text-[13px] font-medium leading-tight transition-colors',
                                isActive ? 'text-text-primary' : 'text-text-secondary group-hover:text-text-primary'
                              )}
                            >
                              {label}
                            </span>
                            {isAlpha && (
                              <sup className="text-[7px] font-bold uppercase tracking-wider text-amber-400/80 -top-1 relative ml-0.5 select-none">
                                alpha
                              </sup>
                            )}
                          </span>
                          <span className="text-[11px] text-text-muted leading-tight truncate">
                            {description}
                          </span>
                        </div>
                      </>
                    )}
                  </NavLink>
                );
              })}
            </nav>
          ) : (
            /* ── Collapsed: compact icon row ── */
            <nav className="flex items-center gap-1 px-2 py-1">
              {primaryNavItems.map(({ to, icon: Icon, label, end, requiresAuth }) => {
                const isLocked = !user && requiresAuth;

                if (isLocked) {
                  return (
                    <Link
                      key={to}
                      to="/signup"
                      title={`${label} — Sign up to access`}
                      className="flex items-center justify-center p-2 rounded-lg transition-colors opacity-40 text-text-muted"
                    >
                      <Icon className="w-[18px] h-[18px]" />
                    </Link>
                  );
                }

                return (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    title={label}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center justify-center p-2 rounded-lg transition-colors',
                        isActive
                          ? 'bg-surface-light text-text-primary'
                          : 'text-text-muted hover:bg-surface-light/50 hover:text-text-secondary'
                      )
                    }
                  >
                    <Icon className="w-[18px] h-[18px]" />
                  </NavLink>
                );
              })}
            </nav>
          )}

          {user?.is_master_user && (
            <>
              <div className="pt-4 mt-4 border-t border-border">
                <p className="px-4 py-1 text-xs font-medium text-text-muted uppercase tracking-wider">
                  Admin
                </p>
              </div>
              <nav className="space-y-1">
                {adminNavItems.map(({ to, icon: Icon, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) =>
                      clsx(
                        'flex items-center gap-3 px-3 py-2.5 rounded-lg border-l-2 border-transparent transition-colors duration-150',
                        isActive
                          ? 'bg-surface-light/50 text-text-primary border-primary/20'
                          : 'text-text-secondary hover:bg-surface-light/30 hover:text-text-primary'
                      )
                    }
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-[13px] font-medium">{label}</span>
                  </NavLink>
                ))}
              </nav>
            </>
          )}
        </div>

      {children && (
        <div className="flex-1 flex flex-col overflow-y-auto border-t border-border">
          {children}
        </div>
      )}

      {/* Credit Balance — visible for authenticated users */}
      {user && (
        <div className="px-3 py-2 border-t border-border mt-auto">
          <Link
            to="/dashboard/plans"
            className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-surface-light/20 transition-colors"
          >
            <Coins className="w-3.5 h-3.5 text-text-muted flex-shrink-0" />
            {isUnlimited ? (
              <span className="text-xs text-text-muted">Unlimited</span>
            ) : (
              <span
                className={clsx(
                  'text-xs',
                  balance !== null && balance <= 0 ? 'text-error' :
                  balance !== null && balance < 200 ? 'text-warning' :
                  'text-text-muted'
                )}
              >
                {balance?.toLocaleString() ?? 0} credits
              </span>
            )}
          </Link>
        </div>
      )}

      {/* Share Feedback — always visible at the bottom */}
      <div className={clsx('px-3 py-2 border-t border-border', !user && 'mt-auto')}>
        <Link
          to="/feedback"
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-text-secondary hover:bg-surface-light/30 hover:text-text-primary transition-colors duration-150"
          title="Share Feedback"
        >
          <MessageSquare className="w-4 h-4 flex-shrink-0 text-text-muted" />
          <span className="text-[13px] font-medium">Share Feedback</span>
        </Link>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-40 bg-surface border-b border-border">
        <div className="flex items-center justify-between p-4">
          <Logo size="sm" linkTo="/dashboard" />
          <button
            onClick={() => setIsOpen(true)}
            className="p-2 rounded-lg text-text-secondary hover:bg-surface-light hover:text-text-primary transition-colors"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </header>

      {/* Mobile sidebar overlay */}
      {isOpen && !isDesktop && (
        <div
          className="md:hidden fixed inset-0 z-50 bg-background/60 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'bg-surface border-r border-border flex flex-col h-screen',
          'fixed md:sticky top-0 left-0 z-50',
          'transform transition-all duration-300 ease-in-out',
          isCollapsed ? 'w-72 md:w-12' : 'w-72 md:w-64',
          isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        )}
      >
        {isCollapsed && isDesktop ? collapsedContent : expandedContent}
      </aside>
    </>
  );
}
